import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, ExternalLink, MessageSquare, AlertTriangle, ChevronUp, ChevronDown, ChevronsUpDown, Trash2, Loader2, CheckSquare, Square } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

// A flat, sortable, filterable list of every onboarding client across every
// partner board — the "ticket queue" view the ops team wanted alongside the
// Kanban. Each row deep-links to the Kanban card so the drawer + canned
// response picker stay one source of truth.

// Column headers are clickable to sort. Click once → asc, twice → desc,
// three times → clear back to default (created_at desc). The "Submitted"
// column shows how long ago the ticket was created with a stale-colour
// pill (green <7d, slate 7-14d, amber 15-30d, red 30d+) so aging
// tickets never quietly rot at the bottom of the list.

const COLUMNS = [
  { key: "business_name", label: "Business", accessor: (r) => (r.business_name || r.contact_name || "").toLowerCase() },
  { key: "contact_name",  label: "Contact",  accessor: (r) => (r.contact_name || "").toLowerCase() },
  { key: "partner_code",  label: "Partner",  accessor: (r) => (r.partner_code || "").toLowerCase() },
  { key: "stage",         label: "Stage",    accessor: (r) => (r.stage || "").toLowerCase() },
  { key: "created_at",    label: "Submitted", accessor: (r) => r.created_at ? new Date(r.created_at).getTime() : 0 },
  { key: "updated_at",    label: "Updated",  accessor: (r) => (r.updated_at || r.created_at) ? new Date(r.updated_at || r.created_at).getTime() : 0 },
  { key: "syncro",        label: "Syncro",   accessor: (r) => r.syncro_ticket_number || "" },
];

function StaleChip({ isoDate }) {
  if (!isoDate) return <span className="text-slate-500 text-[11px]">—</span>;
  const created = new Date(isoDate);
  const days = Math.floor((Date.now() - created) / 86400000);
  if (Number.isNaN(days)) return <span className="text-slate-500 text-[11px]">—</span>;
  // Flattened from the nested-ternary version (v1.10.99) — each rung is
  // a distinct staleness bucket so the pill's tone should read top-down.
  let label;
  if (days <= 0) label = "today";
  else if (days === 1) label = "1d ago";
  else label = `${days}d ago`;
  let tone;
  if (days >= 30)      tone = "bg-red-500/15 border-red-500/50 text-red-300";
  else if (days >= 15) tone = "bg-amber-500/15 border-amber-500/50 text-amber-300";
  else if (days >= 7)  tone = "bg-slate-800 border-slate-700 text-slate-300";
  else                 tone = "bg-emerald-500/10 border-emerald-500/40 text-emerald-300";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-mono ${tone}`}
      title={created.toLocaleString()}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sort-column primitives — hoisted OUT of the parent component so React
// treats them as stable references across renders (per code-review fix
// v1.10.98). Keeping them nested previously required a
// react/no-unstable-nested-components eslint-disable on each
// declaration; hoisting them removes the disable and stops React from
// re-mounting the <th>s on every parent state change.
// Both accept the current sort state + a cycleSort setter as props.
// ---------------------------------------------------------------------------
function SortIcon({ colKey, sort }) {
  if (sort.key !== colKey) return <ChevronsUpDown className="w-3 h-3 opacity-40" />;
  return sort.dir === "asc"
    ? <ChevronUp className="w-3 h-3 text-cyan-300" />
    : <ChevronDown className="w-3 h-3 text-cyan-300" />;
}

function Th({ colKey, label, className = "", sort, cycleSort }) {
  return (
    <th className={`text-left px-4 py-3 font-semibold ${className}`}>
      <button
        type="button"
        onClick={() => cycleSort(colKey)}
        data-testid={`tickets-sort-${colKey}`}
        className="inline-flex items-center gap-1.5 uppercase tracking-widest text-xs hover:text-cyan-300 transition-colors"
      >
        {label}
        <SortIcon colKey={colKey} sort={sort} />
      </button>
    </th>
  );
}

// Persist the user's chosen sort in localStorage so it survives
// refreshes and new sessions. Different key from the partner page so
// shared machines don't cross-contaminate the two views.
const SORT_STORAGE_KEY = "admin-tickets-sort";
function loadSort() {
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY);
    if (!raw) return { key: "created_at", dir: "desc" };
    const parsed = JSON.parse(raw);
    const valid = COLUMNS.some(c => c.key === parsed?.key);
    if (parsed && (valid || parsed.key === null)
        && (parsed.dir === "asc" || parsed.dir === "desc")) {
      return parsed;
    }
  } catch (_) { /* fall through to default */ }
  return { key: "created_at", dir: "desc" };
}

export default function AdminTickets() {
  const [rows, setRows] = useState(null);
  // Full partner catalogue — loaded once so the filter dropdown lists
  // every partner in the system, not just ones that currently have
  // tickets. Lets ops filter to a partner with zero tickets to confirm
  // the queue is genuinely empty rather than mysteriously missing.
  const [allPartners, setAllPartners] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  // Seed from ?partner= so links like /admin/tickets?partner=macktronix
  // land on a pre-filtered view — matches AdminPartnerDetail's deep link.
  const [partnerFilter, setPartnerFilter] = useState(
    (searchParams.get("partner") || "").toLowerCase()
  );
  const [flagFilter, setFlagFilter] = useState("");
  // Sort state: { key, dir } — dir is "asc" | "desc" | null (null = default).
  // v1.10.113: hydrated from localStorage so ops' preferred order sticks
  // across page refreshes and re-logins.
  const [sort, setSort] = useState(loadSort);
  useEffect(() => {
    try {
      localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort));
    } catch (_) { /* private browsing / quota — ignore */ }
  }, [sort]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Bulk-delete selection state — keyed by client id (v1.10.91). Sits
  // outside the DeleteTicketModal so it survives modal open/close and
  // sort/filter changes. When the filtered set changes, we prune ids no
  // longer visible so the "N selected" badge never overcounts.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/commissions/tickets");
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load tickets");
    }
  }
  useEffect(() => {
    load();
    // Prime the partner dropdown from the tenant's partner catalogue —
    // independent of which partners currently have tickets.
    api.get("/commissions/partners")
      .then(({ data }) => setAllPartners(Array.isArray(data) ? data : (data.partners || data.rows || [])))
      .catch(() => setAllPartners([]));
    const t = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);

  // Reflect the current partner filter back into the URL so refreshing
  // or sharing the URL preserves the view.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (partnerFilter) next.set("partner", partnerFilter);
    else next.delete("partner");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [partnerFilter, searchParams, setSearchParams]);

  const partners = useMemo(() => {
    // Union of tenant catalogue + partners seen in tickets, deduped.
    // Ordered by name/code for stable rendering.
    const map = new Map();
    allPartners.forEach(p => {
      if (p.code) map.set(p.code, p.name || p.code);
    });
    (rows || []).forEach(r => {
      if (r.partner_code && !map.has(r.partner_code)) {
        map.set(r.partner_code, r.partner_code);
      }
    });
    return Array.from(map.entries())
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, allPartners]);
  const stages = useMemo(() => {
    const s = new Set();
    (rows || []).forEach(r => r.stage && s.add(r.stage));
    return Array.from(s);
  }, [rows]);

  const filtered = useMemo(() => {
    let out = rows || [];
    const term = q.trim().toLowerCase();
    if (term) {
      out = out.filter(r => {
        const bag = [r.business_name, r.contact_name, r.email, r.phone,
                     r.service_address, r.syncro_ticket_number].join(" ").toLowerCase();
        return bag.includes(term);
      });
    }
    if (stageFilter) out = out.filter(r => r.stage === stageFilter);
    if (partnerFilter) out = out.filter(r => r.partner_code === partnerFilter);
    if (flagFilter === "plan_mismatch") out = out.filter(r => r.plan_mismatch?.is_mismatch && !r.plan_mismatch?.acknowledged);
    if (flagFilter === "signed") out = out.filter(r => !!r.linked_signup_id);
    if (flagFilter === "no_syncro") out = out.filter(r => !r.syncro_ticket_id);
    // Apply sort
    if (sort.key && sort.dir) {
      const col = COLUMNS.find(c => c.key === sort.key);
      if (col) {
        const dir = sort.dir === "asc" ? 1 : -1;
        out = [...out].sort((a, b) => {
          const av = col.accessor(a);
          const bv = col.accessor(b);
          if (av < bv) return -1 * dir;
          if (av > bv) return  1 * dir;
          return 0;
        });
      }
    }
    return out;
  }, [rows, q, stageFilter, partnerFilter, flagFilter, sort]);

  function cycleSort(key) {
    setSort((cur) => {
      if (cur.key !== key) return { key, dir: "asc" };
      if (cur.dir === "asc") return { key, dir: "desc" };
      // Third click → back to default (created_at desc)
      return { key: "created_at", dir: "desc" };
    });
  }

  // Sort-column helpers ``SortIcon`` / ``Th`` live at the top of this
  // module (hoisted in v1.10.98 to satisfy react/no-unstable-nested-
  // components). They're wired via props below.

  return (
    <div className="space-y-5" data-testid="admin-tickets-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Tickets</h1>
          <p className="text-sm text-slate-400 mt-1">
            Every onboarding customer across every partner in one flat list. Click a row to open the card drawer with canned responses, notes, and Syncro sync. Click any column header to sort.
          </p>
        </div>
        <div className="text-xs text-slate-500 font-mono">
          {filtered.length} / {rows?.length || 0}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="tickets-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone, address, ticket #"
            className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-800 focus:border-cyan-500 outline-none rounded-lg text-sm"
          />
        </div>
        <select data-testid="tickets-filter-stage" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} className="bg-slate-900 border border-slate-800 rounded-lg text-sm px-3 py-2">
          <option value="">All stages</option>
          {stages.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select data-testid="tickets-filter-partner" value={partnerFilter} onChange={(e) => setPartnerFilter(e.target.value)} className="bg-slate-900 border border-slate-800 rounded-lg text-sm px-3 py-2">
          <option value="">All partners ({partners.length})</option>
          {partners.map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
        </select>
        <select data-testid="tickets-filter-flag" value={flagFilter} onChange={(e) => setFlagFilter(e.target.value)} className="bg-slate-900 border border-slate-800 rounded-lg text-sm px-3 py-2">
          <option value="">All flags</option>
          <option value="plan_mismatch">Plan mismatch unacked</option>
          <option value="signed">Signed only</option>
          <option value="no_syncro">Missing Syncro ticket</option>
        </select>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
        {/* Bulk-action bar — appears only when at least one row is
            selected. Left-anchored count + right-anchored actions so
            the layout doesn't shift on select-all. */}
        {selected.size > 0 && (
          <div
            data-testid="bulk-action-bar"
            className="flex items-center justify-between gap-3 border-b border-slate-800 bg-cyan-500/5 px-4 py-2.5"
          >
            <div className="text-sm text-cyan-200 font-semibold" data-testid="bulk-selected-count">
              {selected.size} selected
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                data-testid="bulk-clear-btn"
                className="rounded-lg border border-slate-800 hover:bg-slate-950 px-3 py-1.5 text-xs text-slate-300"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setBulkConfirmOpen(true)}
                data-testid="bulk-delete-btn"
                className="inline-flex items-center gap-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 text-xs font-semibold"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete {selected.size} ticket{selected.size === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-slate-950/50 border-b border-slate-800 text-slate-500">
            <tr>
              <th className="w-10 px-3 py-3">
                <button
                  type="button"
                  onClick={() => {
                    const allIds = filtered.map((r) => r.id);
                    const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
                    if (allSelected) {
                      setSelected(new Set());
                    } else {
                      setSelected(new Set(allIds));
                    }
                  }}
                  data-testid="bulk-select-all"
                  title="Select / clear all visible rows"
                  className="text-slate-500 hover:text-cyan-300 transition-colors"
                >
                  {filtered.length > 0 && filtered.every((r) => selected.has(r.id))
                    ? <CheckSquare className="w-4 h-4" />
                    : <Square className="w-4 h-4" />}
                </button>
              </th>
              <Th colKey="business_name" label="Business" sort={sort} cycleSort={cycleSort} />
              <Th colKey="contact_name"  label="Contact" sort={sort} cycleSort={cycleSort} />
              <Th colKey="partner_code"  label="Partner" sort={sort} cycleSort={cycleSort} />
              <Th colKey="stage"         label="Stage" sort={sort} cycleSort={cycleSort} />
              <Th colKey="created_at"    label="Submitted" sort={sort} cycleSort={cycleSort} />
              <Th colKey="updated_at"    label="Updated"   sort={sort} cycleSort={cycleSort} />
              <Th colKey="syncro"        label="Syncro" sort={sort} cycleSort={cycleSort} />
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-widest text-xs">Flags</th>
              <th className="text-right px-4 py-3 font-semibold uppercase tracking-widest text-xs w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500">No matching tickets.</td></tr>
            ) : filtered.map(r => (
              <tr key={r.id} className={`border-b border-slate-800/70 hover:bg-slate-950/60 transition-colors ${selected.has(r.id) ? "bg-cyan-500/5" : ""}`}>
                <td className="px-3 py-3">
                  <button
                    type="button"
                    onClick={() => setSelected((prev) => {
                      const next = new Set(prev);
                      if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                      return next;
                    })}
                    data-testid={`bulk-select-${r.id}`}
                    className="text-slate-500 hover:text-cyan-300 transition-colors"
                  >
                    {selected.has(r.id) ? <CheckSquare className="w-4 h-4 text-cyan-400" /> : <Square className="w-4 h-4" />}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <Link
                    to={`/admin/tickets/${r.id}`}
                    data-testid={`ticket-row-${r.id}`}
                    className="text-slate-100 font-medium hover:text-cyan-300"
                  >
                    {r.business_name || r.contact_name || "(no name)"}
                  </Link>
                  <Link to={`/admin/onboarding/${r.partner_code}`} className="ml-2 text-[10px] uppercase tracking-widest text-slate-500 hover:text-cyan-300">Kanban</Link>
                  <div className="text-[11px] text-slate-500 truncate max-w-xs">{r.service_address}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-slate-200">{r.contact_name || "—"}</div>
                  <div className="text-[11px] text-slate-500 truncate">{r.email} {r.phone && `· ${r.phone}`}</div>
                </td>
                <td className="px-4 py-3"><span className="text-xs text-slate-300 uppercase tracking-widest">{r.partner_code}</span></td>
                <td className="px-4 py-3">
                  <span className="inline-block text-[10px] uppercase tracking-widest bg-slate-800 text-slate-300 rounded px-1.5 py-0.5">
                    {(r.stage || "").replace(/_/g, " ") || "—"}
                  </span>
                </td>
                <td className="px-4 py-3" data-testid={`ticket-submitted-${r.id}`}>
                  <StaleChip isoDate={r.created_at} />
                </td>
                <td className="px-4 py-3" data-testid={`ticket-updated-${r.id}`}>
                  <StaleChip isoDate={r.updated_at || r.created_at} />
                </td>
                <td className="px-4 py-3">
                  {r.syncro_ticket_number ? (
                    <a
                      href={r.syncro_ticket_url || "#"}
                      target="_blank" rel="noreferrer"
                      className="text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1 text-xs"
                    >
                      #{r.syncro_ticket_number} <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : <span className="text-slate-500 text-xs">—</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 flex-wrap">
                    {r.plan_mismatch?.is_mismatch && !r.plan_mismatch?.acknowledged && (
                      <span className="inline-flex items-center gap-1 text-[10px] bg-red-500/20 border border-red-500/60 text-red-200 rounded px-1.5 py-0.5 uppercase tracking-widest font-bold">
                        <AlertTriangle className="w-2.5 h-2.5" /> Mismatch
                      </span>
                    )}
                    {r.linked_signup_id && (
                      <span className="text-[10px] bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 rounded px-1.5 py-0.5 uppercase tracking-widest font-semibold">Signed</span>
                    )}
                    {r.note_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] bg-slate-800 text-slate-300 rounded px-1.5 py-0.5">
                        <MessageSquare className="w-2.5 h-2.5" /> {r.note_count}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(r)}
                    data-testid={`ticket-delete-btn-${r.id}`}
                    title={`Delete ${r.business_name || r.contact_name || "this ticket"}`}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-800 hover:border-red-500/60 hover:bg-red-500/10 hover:text-red-300 text-slate-500 px-2 py-1 text-xs transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DeleteTicketModal
        target={confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onDeleted={(id) => {
          setConfirmDelete(null);
          setRows((prev) => (prev || []).filter((r) => r.id !== id));
        }}
      />
      <BulkDeleteModal
        open={bulkConfirmOpen}
        rows={(rows || []).filter((r) => selected.has(r.id))}
        onCancel={() => setBulkConfirmOpen(false)}
        onDeleted={(deletedIds) => {
          setBulkConfirmOpen(false);
          setRows((prev) => (prev || []).filter((r) => !deletedIds.includes(r.id)));
          setSelected(new Set());
        }}
      />
    </div>
  );
}

function DeleteTicketModal({ target, onCancel, onDeleted }) {
  const [closeSyncro, setCloseSyncro] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setCloseSyncro(true); }, [target?.id]);

  if (!target) return null;
  const label = target.business_name || target.contact_name || "(no name)";
  const hasSyncro = !!target.syncro_ticket_id;

  async function doDelete() {
    setBusy(true);
    try {
      await api.delete(`/commissions/migrations/clients/${target.id}`, {
        params: { close_syncro_ticket: hasSyncro && closeSyncro ? true : false },
      });
      toast.success(`Deleted ${label}${hasSyncro && closeSyncro ? " + closed Syncro ticket" : ""}`);
      onDeleted(target.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="delete-ticket-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 shadow-2xl shadow-black/50"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-slate-800">
          <div className="text-xs uppercase tracking-widest text-red-300 font-semibold inline-flex items-center gap-1.5">
            <Trash2 className="w-3.5 h-3.5" /> Delete ticket
          </div>
          <div className="font-display text-xl mt-1">{label}</div>
          <div className="text-xs text-slate-500 mt-1">
            Removes the Kanban card, activity log, and links to any signup / plan-mismatch rows.
          </div>
        </div>
        {hasSyncro && (
          <label
            className="flex items-start gap-3 p-5 border-b border-slate-800 hover:bg-slate-950/40 cursor-pointer"
            data-testid="delete-ticket-close-syncro-toggle"
          >
            <input
              type="checkbox"
              checked={closeSyncro}
              onChange={(e) => setCloseSyncro(e.target.checked)}
              className="mt-0.5 rounded border-slate-700 bg-slate-950 text-red-500 focus:ring-red-500/30"
            />
            <div>
              <div className="text-sm text-slate-200 font-medium">Also close Syncro ticket #{target.syncro_ticket_number || target.syncro_ticket_id}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Sets the Syncro ticket status to Resolved — Syncro has no hard-delete for tickets.</div>
            </div>
          </label>
        )}
        <div className="p-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="delete-ticket-cancel"
            className="rounded-lg border border-slate-800 hover:bg-slate-950 px-4 py-2 text-sm text-slate-300 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={doDelete}
            disabled={busy}
            data-testid="delete-ticket-confirm"
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Bulk delete confirmation modal — takes a list of selected rows. Shows the
// total count + a preview of the first few, and if ANY selected row has a
// linked Syncro ticket, offers the "also close Syncro tickets" checkbox
// applied uniformly to all rows that have one.
// ---------------------------------------------------------------------------
function BulkDeleteModal({ open, rows, onCancel, onDeleted }) {
  const [closeSyncro, setCloseSyncro] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setCloseSyncro(true); }, [open]);

  if (!open) return null;
  const count = rows.length;
  const withSyncroCount = rows.filter((r) => r.syncro_ticket_id).length;
  const preview = rows.slice(0, 6);

  async function doBulkDelete() {
    setBusy(true);
    try {
      const { data } = await api.post("/commissions/migrations/clients/bulk-delete", {
        client_ids: rows.map((r) => r.id),
        close_syncro_tickets: withSyncroCount > 0 && closeSyncro,
      });
      const msg = `Deleted ${data.deleted_count}/${count}${
        data.skipped_count ? ` — ${data.skipped_count} skipped` : ""
      }${withSyncroCount > 0 && closeSyncro ? ` (Syncro tickets closed for ${withSyncroCount})` : ""}`;
      toast.success(msg);
      onDeleted(data.deleted || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk delete failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="bulk-delete-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-lg rounded-xl border border-slate-800 bg-slate-900 shadow-2xl shadow-black/50"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-slate-800">
          <div className="text-xs uppercase tracking-widest text-red-300 font-semibold inline-flex items-center gap-1.5">
            <Trash2 className="w-3.5 h-3.5" /> Bulk delete
          </div>
          <div className="font-display text-xl mt-1">Delete {count} ticket{count === 1 ? "" : "s"}</div>
          <div className="text-xs text-slate-500 mt-1">
            Removes every selected Kanban card, activity log, and links to any signup / plan-mismatch rows.
          </div>
        </div>
        <div className="max-h-48 overflow-y-auto border-b border-slate-800 divide-y divide-slate-800/50">
          {preview.map((r) => (
            <div key={r.id} className="px-5 py-2 text-xs text-slate-300 truncate">
              {r.business_name || r.contact_name || "(no name)"}
              {r.syncro_ticket_number && (
                <span className="ml-2 text-cyan-400/70">· Syncro #{r.syncro_ticket_number}</span>
              )}
            </div>
          ))}
          {rows.length > preview.length && (
            <div className="px-5 py-2 text-[11px] text-slate-500 italic">…and {rows.length - preview.length} more</div>
          )}
        </div>
        {withSyncroCount > 0 && (
          <label
            className="flex items-start gap-3 p-5 border-b border-slate-800 hover:bg-slate-950/40 cursor-pointer"
            data-testid="bulk-delete-close-syncro-toggle"
          >
            <input
              type="checkbox"
              checked={closeSyncro}
              onChange={(e) => setCloseSyncro(e.target.checked)}
              className="mt-0.5 rounded border-slate-700 bg-slate-950 text-red-500 focus:ring-red-500/30"
            />
            <div>
              <div className="text-sm text-slate-200 font-medium">Also close {withSyncroCount} Syncro ticket{withSyncroCount === 1 ? "" : "s"}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Sets each linked Syncro ticket&rsquo;s status to Resolved — Syncro has no hard-delete for tickets.</div>
            </div>
          </label>
        )}
        <div className="p-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="bulk-delete-cancel"
            className="rounded-lg border border-slate-800 hover:bg-slate-950 px-4 py-2 text-sm text-slate-300 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={doBulkDelete}
            disabled={busy}
            data-testid="bulk-delete-confirm"
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Delete {count}
          </button>
        </div>
      </div>
    </div>
  );
}
